import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { LayoutDashboard, Users, FileText, Receipt, Settings as SettingsIcon, LogOut, Droplets, Package, Truck, Menu, UserCog, Navigation, ClipboardList, Wallet } from "lucide-react";

const items = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/admin/customers", icon: Users, label: "Customers", testid: "nav-customers" },
  { to: "/admin/products", icon: Package, label: "Products", testid: "nav-products" },
  { to: "/admin/orders", icon: ClipboardList, label: "Orders & Quotes", testid: "nav-orders" },
  { to: "/admin/tickets", icon: FileText, label: "Tickets", testid: "nav-tickets" },
  { to: "/admin/invoices", icon: Receipt, label: "Invoices", testid: "nav-invoices" },
  { to: "/driver", icon: Truck, label: "Driver day", testid: "nav-driver" },
  { to: "/admin/track", icon: Navigation, label: "Track driver", testid: "nav-track" },
  { to: "/admin/users", icon: UserCog, label: "Users", testid: "nav-users", adminOnly: true },
  { to: "/admin/finance", icon: Wallet, label: "Finance", testid: "nav-finance", adminOnly: true },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings" },
];

function NavList({ onPick, role }) {
  const visible = items.filter((it) => !it.adminOnly || role === "admin");
  const [lockedCount, setLockedCount] = React.useState(0);
  React.useEffect(() => {
    // Sidebar badge: poll the locked-account count every 60s so the admin sees
    // brute-force lockouts immediately without leaving Users.
    let cancelled = false;
    const tick = () => api.get("/admin/locked-accounts-count")
      .then((r) => { if (!cancelled) setLockedCount(r.data?.locked || 0); })
      .catch(() => {});
    if (role === "admin" || role === "secretary") {
      tick();
      const id = setInterval(tick, 60000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return undefined;
  }, [role]);
  return (
    <nav className="flex-1 p-2 space-y-1" data-testid="admin-nav">
      {visible.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onPick}
          data-testid={it.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
            }`
          }
        >
          <it.icon className="w-4 h-4" />
          <span className="flex-1">{it.label}</span>
          {it.to === "/admin/users" && lockedCount > 0 && (
            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold" title={`${lockedCount} account(s) locked out`} data-testid="locked-account-badge">{lockedCount}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarChrome({ user, onSignOut }) {
  return (
    <>
      <div className="p-5 border-b border-border">
        <div className="flex items-baseline gap-1">
          <Droplets className="w-5 h-5 mr-1 text-primary" />
          <span className="text-lg font-black tracking-tighter text-primary font-display">CATSKILLS</span>
          <span className="text-xs font-light tracking-wide">POOL</span>
        </div>
        <p className="overline mt-3">Operations Console</p>
      </div>
      <NavList role={user?.role} />
      <div className="p-3 border-t border-border">
        <div className="text-xs text-muted-foreground mb-2 font-mono truncate">{user?.email}</div>
        <Button variant="outline" size="sm" className="w-full rounded-sm" onClick={onSignOut}
                data-testid="logout-button">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add("admin-portal-shell");
    return () => document.body.classList.remove("admin-portal-shell");
  }, []);

  const signOut = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background text-foreground">
      {/* Desktop sidebar — sticky so it stays visible while the page scrolls.
          Using h-screen + overflow-y-auto keeps the sidebar itself scrollable
          if the nav list ever grows past viewport, without capturing scroll
          for the main content area. */}
      <aside className="hidden md:flex w-60 border-r border-border bg-surface flex-col no-print sticky top-0 h-screen overflow-y-auto self-start">
        <SidebarChrome user={user} onSignOut={signOut} />
      </aside>

      {/* Mobile top bar */}
      <header className="admin-mobile-pwa-header md:hidden flex items-center justify-between px-4 h-14 border-b border-border bg-surface no-print sticky top-0 z-30" data-testid="admin-mobile-header">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="h-10 w-10 inline-flex items-center justify-center rounded-sm hover:bg-secondary"
          data-testid="open-mobile-nav"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-baseline gap-1">
          <Droplets className="w-4 h-4 mr-0.5 text-primary" />
          <span className="text-base font-black tracking-tighter text-primary font-display">CATSKILLS</span>
          <span className="text-[10px] font-light tracking-wide">POOL</span>
        </div>
        <div className="w-10" aria-hidden />
      </header>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-72 p-0 flex flex-col"
          style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          closeTestId="admin-mobile-nav-close"
          data-testid="mobile-nav-drawer"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Admin navigation and account controls</SheetDescription>
          <div className="p-5 border-b border-border">
            <div className="flex items-baseline gap-1">
              <Droplets className="w-5 h-5 mr-1 text-primary" />
              <span className="text-lg font-black tracking-tighter text-primary font-display">CATSKILLS</span>
              <span className="text-xs font-light tracking-wide">POOL</span>
            </div>
            <p className="overline mt-3">Operations Console</p>
          </div>
          <NavList onPick={() => setMobileOpen(false)} role={user?.role} />
          <div className="p-3 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2 font-mono truncate">{user?.email}</div>
            <Button variant="outline" size="sm" className="w-full rounded-sm" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main content area — no internal overflow so the browser's native
          document scroll handles vertical scrolling. This avoids the iOS
          Safari bug where nested `overflow: auto` containers (esp. the
          horizontally-scrollable admin tables inside) can eat touch events
          and prevent the outer page from scrolling. min-w-0 lets flex
          children shrink correctly when tables push horizontal overflow. */}
      <main className="flex-1 min-w-0" data-testid="admin-main">
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
